/**
 * DirectorioList.js - Lista de negocios del directorio
 * Conectado a Firestore via placesService
 * Filtros: Estado (Pendientes/Verificados/Rechazados), Tipo, Categoria, Contacto y busqueda
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { getPlaces, getPlacesStats, verifyPlace } from '../../localizador/services/placesService';

// Categorias por tipo
const categoriasEstablecimiento = [
  { id: 'Veterinaria', nombre: 'Veterinaria' },
  { id: 'Farmacia', nombre: 'Farmacia' },
  { id: 'Parques', nombre: 'Parques' },
  { id: 'Pension', nombre: 'Pension' },
  { id: 'Estilistas', nombre: 'Estilistas' },
  { id: 'Cerrajeria', nombre: 'Cerrajeria' },
  { id: 'Farmacia 24h', nombre: 'Farmacia 24h' },
  { id: 'Conveniencia', nombre: 'Conveniencia' }
];

const categoriasProveedor = [
  { id: 'Paseadores', nombre: 'Paseadores' },
  { id: 'Entrenadores', nombre: 'Entrenadores' },
  { id: 'Cuidadores', nombre: 'Cuidadores' },
  { id: 'Estilistas', nombre: 'Estilistas' }
];

// Estados de contacto
const estadosContacto = [
  { id: 'sin_contactar', nombre: 'Sin contactar' },
  { id: 'intentado', nombre: 'Intentado' },
  { id: 'hablado', nombre: 'Hablado' },
  { id: 'interesado', nombre: 'Interesado' },
  { id: 'rechazado', nombre: 'Rechazado' }
];

function formatTimeAgo(timestamp) {
  if (!timestamp) return '';

  // Convertir Firestore Timestamp a Date
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Hace 1 dia';
  if (diffDays < 7) return `Hace ${diffDays} dias`;
  if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
  return `Hace ${Math.floor(diffDays / 30)} meses`;
}

function formatProximoContacto(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function isContactoUrgente(timestamp) {
  if (!timestamp) return false;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date <= today;
}

function getContactoStatusBadge(status) {
  switch (status) {
    case 'sin_contactar': return { text: 'Sin contactar', color: 'secondary' };
    case 'intentado': return { text: 'Intentado', color: 'info' };
    case 'hablado': return { text: 'Hablado', color: 'primary' };
    case 'interesado': return { text: 'Interesado', color: 'success' };
    case 'rechazado': return { text: 'Rechazado', color: 'danger' };
    default: return { text: 'Sin contactar', color: 'secondary' };
  }
}

export default function DirectorioList({ onSelectNegocio, onNuevoNegocio, addToHistory }) {
  const [filtroEstado, setFiltroEstado] = useState('pendientes');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [filtroContacto, setFiltroContacto] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  const [negocios, setNegocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ pendientes: 0, verificados: 0, rechazados: 0 });

  // Cargar lugares desde Firestore
  const cargarNegocios = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Construir filtros para la API
      const filtros = {};

      // Mapear filtro de estado
      if (filtroEstado === 'pendientes') {
        filtros.status = 'pendiente';
      } else if (filtroEstado === 'verificados') {
        filtros.status = 'verificado';
      } else if (filtroEstado === 'rechazados') {
        filtros.status = 'rechazado';
      }

      if (filtroTipo !== 'todos') {
        filtros.tipo = filtroTipo === 'establecimientos' ? 'establecimiento' : 'proveedor';
      }

      if (filtroCategoria !== 'todas') {
        filtros.categoria = filtroCategoria;
      }

      if (filtroContacto !== 'todos') {
        filtros.contactoStatus = filtroContacto;
      }

      if (busqueda.trim()) {
        filtros.busqueda = busqueda.trim();
      }

      // En Admin Panel: NO activar fallback a Google Places
      // (La colección 'places' ahora solo contiene datos propios)
      filtros.noFallback = true;

      const lugares = await getPlaces(filtros);
      setNegocios(lugares);

      // Cargar estadisticas
      try {
        const estadisticas = await getPlacesStats();
        setStats(estadisticas);
      } catch (statsErr) {
        console.warn('No se pudieron cargar estadisticas:', statsErr);
      }

    } catch (err) {
      console.error('Error cargando negocios:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroTipo, filtroCategoria, filtroContacto, busqueda]);

  // Cargar al montar y cuando cambian los filtros
  useEffect(() => {
    cargarNegocios();
  }, [cargarNegocios]);

  // Categorias disponibles segun el tipo seleccionado
  const categoriasDisponibles = useMemo(() => {
    if (filtroTipo === 'todos') return [];
    if (filtroTipo === 'establecimientos') return categoriasEstablecimiento;
    if (filtroTipo === 'proveedores') return categoriasProveedor;
    return [];
  }, [filtroTipo]);

  // Reset categoria cuando cambia el tipo
  const handleTipoChange = (nuevoTipo) => {
    setFiltroTipo(nuevoTipo);
    setFiltroCategoria('todas');
  };

  const handleSelectNegocio = (negocio) => {
    if (addToHistory) {
      addToHistory(`Editando negocio: ${negocio.nombre}`);
    }
    onSelectNegocio(negocio);
  };

  // Verificar lugar rapidamente desde la lista
  const handleVerificarRapido = async (e, negocio) => {
    e.stopPropagation();
    if (!window.confirm(`Verificar "${negocio.nombre}"?`)) return;

    try {
      await verifyPlace(negocio.id);
      if (addToHistory) {
        addToHistory(`Lugar verificado: ${negocio.nombre}`);
      }
      cargarNegocios(); // Recargar lista
    } catch (err) {
      alert('Error al verificar: ' + err.message);
    }
  };

  // Funcion para imprimir
  const handleImprimir = useCallback(() => {
    if (addToHistory) {
      addToHistory(`Imprimiendo listado (${negocios.length} negocios)`);
    }

    // Agrupar por categoria
    const porCategoria = negocios.reduce((acc, neg) => {
      const cat = neg.categoria || 'Sin categoria';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(neg);
      return acc;
    }, {});

    // Generar filtros aplicados texto
    const filtrosTexto = [];
    const estadoTexto = filtroEstado === 'pendientes' ? 'Pendientes' :
                        filtroEstado === 'verificados' ? 'Verificados' : 'Rechazados';
    filtrosTexto.push(`Estado: ${estadoTexto}`);
    if (filtroTipo !== 'todos') {
      filtrosTexto.push(`Tipo: ${filtroTipo === 'establecimientos' ? 'Establecimientos' : 'Proveedores'}`);
    }
    if (filtroCategoria !== 'todas') {
      const catNombre = categoriasDisponibles.find(c => c.id === filtroCategoria)?.nombre || filtroCategoria;
      filtrosTexto.push(`Categoria: ${catNombre}`);
    }
    if (filtroContacto !== 'todos') {
      const contactoNombre = estadosContacto.find(c => c.id === filtroContacto)?.nombre || filtroContacto;
      filtrosTexto.push(`Contacto: ${contactoNombre}`);
    }
    if (busqueda.trim()) {
      filtrosTexto.push(`Busqueda: "${busqueda}"`);
    }

    // Crear ventana de impresion
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Directorio - Listado</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 24px; margin-bottom: 5px; }
          .header-info { color: #666; font-size: 12px; margin-bottom: 20px; }
          .filtros { background: #f5f5f5; padding: 10px; margin-bottom: 20px; font-size: 12px; }
          .categoria-header { background: #333; color: white; padding: 8px 12px; margin-top: 20px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background: #f9f9f9; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; }
          .badge-pendiente { background: #ffc107; color: #000; }
          .badge-verificado { background: #28a745; color: #fff; }
          .badge-rechazado { background: #dc3545; color: #fff; }
          @media print {
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>Directorio de Negocios</h1>
        <div class="header-info">
          Fecha de impresion: ${new Date().toLocaleString('es-MX')}
        </div>
        <div class="filtros">
          <strong>Filtros aplicados:</strong> ${filtrosTexto.join(' | ')}
          <br>
          <strong>Total de registros:</strong> ${negocios.length}
        </div>

        ${Object.entries(porCategoria).map(([categoria, negs]) => `
          <div class="categoria-header">${categoria} (${negs.length})</div>
          <table>
            <thead>
              <tr>
                <th style="width: 25%">Nombre</th>
                <th style="width: 15%">Telefono</th>
                <th style="width: 25%">Direccion/Zona</th>
                <th style="width: 15%">Contacto</th>
                <th style="width: 10%">Estado</th>
              </tr>
            </thead>
            <tbody>
              ${negs.map(neg => `
                <tr>
                  <td><strong>${neg.nombre}</strong></td>
                  <td>${neg.telefono || '-'}</td>
                  <td>${neg.direccion || (neg.zonas?.join(', ')) || '-'}</td>
                  <td>${getContactoStatusBadge(neg.contacto?.status).text}</td>
                  <td>
                    <span class="badge badge-${neg.status}">
                      ${neg.status === 'pendiente' ? 'Pendiente' : neg.status === 'verificado' ? 'Verificado' : 'Rechazado'}
                    </span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `).join('')}

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }, [negocios, filtroEstado, filtroTipo, filtroCategoria, filtroContacto, busqueda, categoriasDisponibles, addToHistory]);

  return (
    <div className="card">
      <div className="card-header bg-white">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-building me-2"></i>
            Directorio
          </h5>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={cargarNegocios}
            disabled={loading}
          >
            <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
          </button>
        </div>
      </div>
      <div className="card-body">
        {/* Barra de busqueda */}
        <div className="mb-3">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar negocio..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            {busqueda && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setBusqueda('')}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>

        {/* Tabs de estado: Pendientes / Verificados / Rechazados */}
        <div className="btn-group mb-3 w-100" role="group">
          <button
            className={`btn ${filtroEstado === 'pendientes' ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setFiltroEstado('pendientes')}
          >
            Pendientes ({stats.pendientes || 0})
          </button>
          <button
            className={`btn ${filtroEstado === 'verificados' ? 'btn-success' : 'btn-outline-success'}`}
            onClick={() => setFiltroEstado('verificados')}
          >
            Verificados ({stats.verificados || 0})
          </button>
          <button
            className={`btn ${filtroEstado === 'rechazados' ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={() => setFiltroEstado('rechazados')}
          >
            Rechazados ({stats.rechazados || 0})
          </button>
        </div>

        {/* Dropdowns de filtro: Tipo, Categoria y Contacto */}
        <div className="row mb-3 g-2">
          <div className="col-4">
            <label className="form-label small text-muted mb-1">Tipo</label>
            <select
              className="form-select form-select-sm"
              value={filtroTipo}
              onChange={(e) => handleTipoChange(e.target.value)}
            >
              <option value="todos">Todos</option>
              <option value="establecimientos">Establecimientos</option>
              <option value="proveedores">Proveedores</option>
            </select>
          </div>
          <div className="col-4">
            <label className="form-label small text-muted mb-1">Categoria</label>
            <select
              className="form-select form-select-sm"
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              disabled={filtroTipo === 'todos'}
            >
              <option value="todas">Todas</option>
              {categoriasDisponibles.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.nombre}</option>
              ))}
            </select>
          </div>
          <div className="col-4">
            <label className="form-label small text-muted mb-1">Contacto</label>
            <select
              className="form-select form-select-sm"
              value={filtroContacto}
              onChange={(e) => setFiltroContacto(e.target.value)}
            >
              <option value="todos">Todos</option>
              {estadosContacto.map(estado => (
                <option key={estado.id} value={estado.id}>{estado.nombre}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-danger py-2">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        )}

        {/* Lista de negocios */}
        {!loading && (
          <div className="list-group" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {negocios.length > 0 ? (
              negocios.map(negocio => {
                const contacto = negocio.contacto || {};
                const contactoStatus = getContactoStatusBadge(contacto.status);
                const proximoContacto = contacto.proximoContacto;
                const urgente = isContactoUrgente(proximoContacto);

                return (
                  <button
                    key={negocio.id}
                    className={`list-group-item list-group-item-action ${urgente ? 'list-group-item-warning' : ''}`}
                    onClick={() => handleSelectNegocio(negocio)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h6 className="mb-1 d-flex align-items-center gap-2">
                          {negocio.nombre}
                          {urgente && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: '0.65rem' }}>
                              <i className="bi bi-exclamation-triangle"></i>
                            </span>
                          )}
                        </h6>
                        <small className="text-muted d-block">
                          {negocio.tipo === 'establecimiento' ? 'Establecimiento' : 'Proveedor'}
                          {' · '}
                          <span className={`text-${contactoStatus.color}`}>{contactoStatus.text}</span>
                          {proximoContacto && (
                            <> · Prox: {formatProximoContacto(proximoContacto)}</>
                          )}
                          {contacto.asignadoNombre && (
                            <> · {contacto.asignadoNombre}</>
                          )}
                        </small>
                        {negocio.source === 'user_suggestion' && (
                          <small className="text-info d-block">
                            <i className="bi bi-person-badge me-1"></i>
                            Sugerido por usuario
                          </small>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 ms-2">
                        {negocio.status === 'pendiente' && (
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={(e) => handleVerificarRapido(e, negocio)}
                            title="Verificar"
                          >
                            <i className="bi bi-check-lg"></i>
                          </button>
                        )}
                        <span className={`badge ${
                          negocio.status === 'pendiente' ? 'bg-warning text-dark' :
                          negocio.status === 'verificado' ? 'bg-success' : 'bg-danger'
                        }`}>
                          {negocio.status === 'pendiente' ? 'Pend' :
                           negocio.status === 'verificado' ? 'Verif' : 'Rech'}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="list-group-item text-center text-muted py-4">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                No se encontraron negocios
              </div>
            )}
          </div>
        )}

        {/* Contador de resultados */}
        {!loading && negocios.length > 0 && (
          <div className="text-muted small mt-2">
            Mostrando {negocios.length} negocios
          </div>
        )}

        {/* Botones de accion */}
        <div className="d-flex gap-2 mt-3">
          <button
            className="btn btn-outline-secondary"
            onClick={handleImprimir}
            disabled={negocios.length === 0 || loading}
          >
            <i className="bi bi-printer me-2"></i>
            Imprimir
          </button>
          <button
            className="btn btn-primary flex-grow-1"
            onClick={onNuevoNegocio}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Agregar negocio
          </button>
        </div>
      </div>

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
